import {Component, OnInit, Optional} from '@angular/core';
import {Translatable, TranslationService} from '@ngmedax/translation';
import {LayoutService} from '@ngmedax/layout';
import {TRANSLATION_THEMING_SCOPE} from '../../../constants';
import {KEYS} from '../../../translation-keys';
import {UiSettingsService} from '../services/ui-settings.service';
import {SettingsThemeService} from '../services/settings-theme.service';

// hack to inject decorator declarations. must occur before class declaration!
export interface AppearanceComponent extends Translatable {}

type SwitchKey = 'theme' | 'editor' | 'submenu' | 'appDesign';

/** Settings key of the design of the fill app, the api-server sends it to the app (pairing, patient assignment) */
const APP_DESIGN_KEY = 'mobileAppDesign';

/** Design of the fill app without a stored value, same default as the api-server */
const APP_DESIGN_DEFAULT = 'modern';

/** One switch of the page with its choices */
interface Switch {
  key: SwitchKey;
  group: 'admin' | 'app';
  icon: string;
  title: string;
  text: string;
  options: {value: string, label: string, icon: string}[];
}

/**
 * Look of the administration for the whole instance (base design, questionnaire editor and position of the sub menu)
 * and the design of the fill app. The config sets the defaults of the administration, a value stored here wins over them
 */
@Component({
  selector: 'app-appearance',
  templateUrl: './appearance.component.html',
  styleUrls: ['./theming.component.css', './appearance.component.css']
})
@Translatable({scope: TRANSLATION_THEMING_SCOPE, keys: KEYS})
export class AppearanceComponent implements OnInit {
  public readonly switches: Switch[] = [
    {
      key: 'theme', group: 'admin', icon: 'fa-paint-brush', title: 'Design',
      text: 'Grundlegende Optik der Verwaltungsoberfläche.',
      options: [{value: 'new', label: 'Modern', icon: 'fa-star-o'}, {value: 'classic', label: 'Klassisch', icon: 'fa-history'}]
    },
    {
      key: 'editor', group: 'admin', icon: 'fa-pencil-square-o', title: 'Fragebogen-Editor',
      text: 'Modern: Bausteine links, Eigenschaften rechts. Klassisch: einspaltig, jede Frage zeigt ihre Eigenschaften selbst.',
      options: [{value: 'new', label: 'Modern', icon: 'fa-columns'}, {value: 'classic', label: 'Klassisch', icon: 'fa-list'}]
    },
    {
      key: 'submenu', group: 'admin', icon: 'fa-sitemap', title: 'Untermenü',
      text: 'Unterseiten eines Bereichs als Reiter über der Seite oder ausgeklappt im Seitenmenü.',
      options: [{value: 'top', label: 'Oben', icon: 'fa-window-maximize'}, {value: 'left', label: 'Links', icon: 'fa-bars'}]
    },
    {
      key: 'appDesign', group: 'app', icon: 'fa-tablet', title: 'Design der Ausfüll-App',
      text: 'Modern: das neue Design seit myMedax 5. Klassisch: die Optik von myMedax 4, z. B. für einen späteren Umstieg. '
        + 'Die Geräte übernehmen die Einstellung beim nächsten Zuweisen eines Patienten.',
      options: [{value: 'modern', label: 'Modern', icon: 'fa-star-o'}, {value: 'classic', label: 'Klassisch', icon: 'fa-history'}]
    }
  ];

  /** Switches of the administration and of the fill app, shown as two groups */
  public readonly adminSwitches = this.switches.filter(item => item.group === 'admin');
  public readonly appSwitches = this.switches.filter(item => item.group === 'app');

  /** Values chosen on this page, null keeps the default */
  public draft: {[key: string]: string | null} = {theme: null, editor: null, submenu: null, appDesign: null};

  /** Stored design of the fill app, null when the default is used */
  private storedAppDesign: string | null = null;

  public constructor(
    @Optional() private translationService: TranslationService,
    private uiSettings: UiSettingsService,
    private settingsApi: SettingsThemeService,
    private layout: LayoutService
  ) {}

  public async ngOnInit() {
    await this.uiSettings.refresh();
    await this.loadAppDesign();
    this.load();
  }

  /** Default for a switch: the config for the administration, the default of the api-server for the fill app */
  public preset(key: SwitchKey): string {
    if (key === 'appDesign') {
      return APP_DESIGN_DEFAULT;
    }

    return this.uiSettings.getDefaults()[key];
  }

  /** Value shown as selected: the stored or chosen one, otherwise the default */
  public selected(key: SwitchKey): string {
    return this.draft[key] || this.preset(key);
  }

  public label(key: SwitchKey, value: string): string {
    const option = this.switches.find(item => item.key === key).options.find(item => item.value === value);
    return option ? this._(option.label) : value;
  }

  public choose(key: SwitchKey, value: string) {
    this.draft[key] = value;
  }

  /** Goes back to the defaults of the config */
  public reset(key: SwitchKey) {
    this.draft[key] = null;
  }

  public get dirty(): boolean {
    return this.isAdminDirty() || this.isAppDirty();
  }

  public async onSave() {
    try {
      this.layout.showPreloader(this._('Wird gespeichert...'));

      if (this.isAdminDirty()) {
        await this.uiSettings.saveSwitches({theme: this.draft.theme, editor: this.draft.editor, submenu: this.draft.submenu});
      }

      if (this.isAppDirty()) {
        // an empty value lets the api-server use its default
        await this.settingsApi.save({[APP_DESIGN_KEY]: this.draft.appDesign || ''});
        this.storedAppDesign = this.draft.appDesign || null;
      }

      this.layout.hidePreloader();
      alert(this._('Die Einstellungen wurden gespeichert.'));
    } catch (error) {
      console.error(error);
      this.layout.hidePreloader();
      alert(this._('Fehler beim Speichern der Einstellungen.'));
    }
  }

  private load() {
    const stored = this.uiSettings.getSettings();
    this.draft = {theme: stored.theme, editor: stored.editor, submenu: stored.submenu, appDesign: this.storedAppDesign};
  }

  /** Reads the stored design of the fill app, unknown values count as default */
  private async loadAppDesign() {
    const value = await this.settingsApi.read(APP_DESIGN_KEY);
    this.storedAppDesign = value === 'modern' || value === 'classic' ? value : null;
  }

  private isAdminDirty(): boolean {
    const stored: any = this.uiSettings.getSettings();
    return this.adminSwitches.some(item => (this.draft[item.key] || null) !== (stored[item.key] || null));
  }

  private isAppDirty(): boolean {
    return (this.draft.appDesign || null) !== this.storedAppDesign;
  }

  public _!: (text: string, variables?: any) => string;
  public readonly KEYS = KEYS;
}
